import {
  Attribute,
  Bucket,
  DateFilter,
  MapLayer,
  MarkerModeName,
  MapSource,
} from "@cartographerio/atlas-map";
import { Feature } from "@cartographerio/geometry";
import { checkExhausted } from "@cartographerio/util";
import {
  expr,
  MapboxExpr,
  MapboxLayer,
  MapboxLayerLayout,
  MapboxLayerPaint,
  MapboxLayerType,
} from "../mapbox";
import { createDateFilter } from "./date";
import { notSelectedFilter, selectedFilter } from "./feature";
import { attributeFilter } from "./filter";
import {
  fillLayerId,
  lineLayerId,
  selectedLayerId,
  simplifiedLayerId,
} from "./layerId";
import {
  circleLayoutExpr,
  circlePaintExpr,
  fillLayoutExpr,
  fillPaintExpr,
  lineLayoutExpr,
  linePaintExpr,
} from "./paint";
import { mapboxSourceId, mapboxSourceLayerId } from "./source";

export function createMapboxLayers(
  layer: MapLayer,
  attr: Attribute,
  buckets: Bucket[],
  dateFilter?: DateFilter,
  selectedFeatures: Feature[] = []
): MapboxLayer[] {
  const { layerId, source } = layer;

  const ans: MapboxLayer[] = [];

  let simplifyZoom: number | undefined;
  switch (source.type) {
    case "CartographerSource":
      simplifyZoom = source.simplifyZoom;
      break;

    case "RemoteGeojsonSource":
    case "RemoteTileSource":
    case "LocalGeojsonSource":
      simplifyZoom = undefined;
      break;

    default:
      checkExhausted(source);
  }

  function basicLayer(
    type: MapboxLayerType,
    id: string,
    source: MapSource,
    simplify: boolean = false
  ): MapboxLayer {
    switch (source.type) {
      case "RemoteGeojsonSource":
      case "LocalGeojsonSource":
        return {
          id,
          type,
          source: mapboxSourceId(source),
        };

      case "RemoteTileSource":
        return {
          id,
          type,
          source: mapboxSourceId(source),
          "source-layer": mapboxSourceLayerId(source),
        };

      case "CartographerSource":
        switch (source.forceSourceType) {
          case "tiles":
            return {
              id,
              type,
              source: mapboxSourceId(source, simplify),
              "source-layer": mapboxSourceLayerId(source),
            };

          case "geojson":
          case undefined:
            return {
              id,
              type,
              source: mapboxSourceId(source, simplify),
            };

          default:
            return checkExhausted(source.forceSourceType);
        }

      default:
        return checkExhausted(source);
    }
  }

  function withRegularZoom(layer: MapboxLayer): MapboxLayer {
    if (simplifyZoom == null) {
      return layer;
    } else {
      return { ...layer, minzoom: simplifyZoom };
    }
  }

  function withSimplifiedZoom(layer: MapboxLayer): MapboxLayer {
    if (simplifyZoom == null) {
      return layer;
    } else {
      return { ...layer, maxzoom: simplifyZoom };
    }
  }

  function withFilters(
    filters: (MapboxExpr | true)[],
    layer: MapboxLayer
  ): MapboxLayer {
    if (filters.length === 0) {
      return layer;
    }
    return { ...layer, filter: expr.all(...filters) };
  }

  function withPaint(
    paint: MapboxLayer["paint"],
    layer: MapboxLayer
  ): MapboxLayer {
    return { ...layer, paint };
  }

  function withLayout(
    layout: MapboxLayer["layout"],
    layer: MapboxLayer
  ): MapboxLayer {
    return { ...layer, layout };
  }

  let propertyExpr: MapboxExpr;
  switch (attr.type) {
    case "PropertyAttribute":
      propertyExpr = expr.get(attr.propertyName);
      break;

    case "SimpleAttribute":
      propertyExpr = expr.get("noPropertyDefinedForSimpleAttribute");
      break;

    default:
      checkExhausted(attr);
  }

  const dateFilterExpr = dateFilter ? createDateFilter(dateFilter) : true;
  const attributeFilterExpr = attributeFilter(attr, buckets) ?? true;
  const selectedFilterExpr = selectedFilter(layer, selectedFeatures);
  const notSelectedFilterExpr = notSelectedFilter(layer, selectedFeatures);

  function pushLayer(
    layer: MapboxLayer,
    getPaint: (style: MarkerModeName) => MapboxLayerPaint,
    getLayout: (style: MarkerModeName) => MapboxLayerLayout
  ): void {
    ans.push(
      withFilters(
        [notSelectedFilterExpr, attributeFilterExpr, dateFilterExpr],
        withLayout(getLayout("normal"), withPaint(getPaint("normal"), layer))
      )
    );

    // Also add a layer that displays selected features:

    const selectedLayer: MapboxLayer = {
      ...layer,
      id: selectedLayerId(layer.id),
    };

    ans.push(
      withFilters(
        [selectedFilterExpr, attributeFilterExpr, dateFilterExpr],
        withPaint(getPaint("selected"), selectedLayer)
      )
    );
  }

  // Add a regular layer:

  switch (layer.type) {
    case "PointLayer":
      pushLayer(
        withRegularZoom(basicLayer("circle", layerId, source)),
        style => circlePaintExpr(propertyExpr, buckets, style),
        style => circleLayoutExpr(propertyExpr, buckets, style)
      );
      break;

    case "LineLayer":
      pushLayer(
        withRegularZoom(basicLayer("line", layerId, source)),
        style => linePaintExpr(propertyExpr, buckets, style),
        style => lineLayoutExpr(propertyExpr, buckets, style)
      );
      break;

    case "PolygonLayer":
      pushLayer(
        withRegularZoom(basicLayer("fill", fillLayerId(layerId), source)),
        style => fillPaintExpr(propertyExpr, buckets, style),
        style => fillLayoutExpr(propertyExpr, buckets, style)
      );

      pushLayer(
        withRegularZoom(basicLayer("line", lineLayerId(layerId), source)),
        style => linePaintExpr(propertyExpr, buckets, style),
        style => lineLayoutExpr(propertyExpr, buckets, style)
      );
      break;
  }

  // Add an optional layer for simplified geometry:

  switch (source.type) {
    case "CartographerSource":
      if (source.simplifyZoom != null) {
        pushLayer(
          withSimplifiedZoom(
            basicLayer("circle", simplifiedLayerId(layerId), source, true)
          ),
          style => circlePaintExpr(propertyExpr, buckets, style),
          style => circleLayoutExpr(propertyExpr, buckets, style)
        );
      }
      break;

    case "RemoteGeojsonSource":
    case "RemoteTileSource":
    case "LocalGeojsonSource":
      break;

    default:
      checkExhausted(source);
  }

  return ans;
}
